import { useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { CartContext } from '../context/CartContext';

const SHARED_CART_CLIENT_ID_KEY = 'sharedCartClientId';

function isTakeaway(tableNo) {
  if (!tableNo) return true;
  const t = String(tableNo).toLowerCase();
  return t === 'take_away' || t === 'take-away';
}

function getOrCreateClientId() {
  let id = sessionStorage.getItem(SHARED_CART_CLIENT_ID_KEY);
  if (!id) {
    id = `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SHARED_CART_CLIENT_ID_KEY, id);
  }
  return id;
}

// 共享购物车 hook
// 参数：restaurantUrl, tableNo, enabled
// 返回：partnerFirstAdd (最近一次"伙伴首次加菜"事件；使用后可调 clearPartnerFirstAdd 清空)
export function useSharedCart(restaurantUrl, tableNo, enabled = true) {
  const { cart, setCartFromRemote, subscribeLocalChange } = useContext(CartContext);
  const [partnerFirstAdd, setPartnerFirstAdd] = useState(null);

  const socketRef = useRef(null);
  const cartRef = useRef(cart);
  cartRef.current = cart;

  useEffect(() => {
    if (!enabled || !restaurantUrl || !tableNo || isTakeaway(tableNo)) {
      setPartnerFirstAdd(null);
      return undefined;
    }

    const clientId = getOrCreateClientId();
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const socket = io(`${serverUrl}/shared-cart`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    socketRef.current = socket;

    let unsubscribe = null;

    const emitJoin = () => {
      socket.emit(
        'join',
        {
          restaurantUrl,
          tableNo,
          clientId,
          localCart: cartRef.current,
        },
        (resp) => {
          if (resp && resp.ok && Array.isArray(resp.cart)) {
            // 服务器返回的 snapshot 覆盖本地（只有在非空时；空的情况下本地已经作为初始上报）
            if (resp.cart.length > 0) {
              setCartFromRemote(resp.cart);
            }
          }
        }
      );
    };

    socket.on('connect', () => {
      emitJoin();
    });

    socket.on('cart:updated', (payload) => {
      if (!payload || payload.fromClientId === clientId) return;
      if (Array.isArray(payload.cart)) {
        setCartFromRemote(payload.cart);
      }
    });

    socket.on('cart:cleared', () => {
      setCartFromRemote([]);
    });

    socket.on('partner:first-add', (payload) => {
      console.log('[sharedCart] received partner:first-add', payload, 'myClientId=', clientId);
      if (!payload || payload.fromClientId === clientId) {
        console.log('[sharedCart] skipped (self or empty)');
        return;
      }
      if (!payload.mainItem) {
        console.log('[sharedCart] skipped (no mainItem)');
        return;
      }
      console.log('[sharedCart] setting partnerFirstAdd');
      setPartnerFirstAdd({
        mainItem: payload.mainItem,
        ts: Date.now(),
      });
    });

    // 订阅本地 cart 变更 → 转成 op 发给服务器
    unsubscribe = subscribeLocalChange(({ op }) => {
      if (!op || !socket.connected) return;
      socket.emit('cart:op', { op });
    });

    return () => {
      if (unsubscribe) unsubscribe();
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantUrl, tableNo, enabled]);

  const clearPartnerFirstAdd = () => setPartnerFirstAdd(null);

  return { partnerFirstAdd, clearPartnerFirstAdd };
}
